// ProductionByParty — management report (2026-06-04).
// Business done (hours + service / expense / admin fees + total) grouped by any
// combination of {party, year, month}, where party = Insured / Underwriter
// (slip-%-allocated) / Broker. Self-contained: owns dimension/group-by/date/name
// filters, fetch, sticky-sortable table (composite sort), and XLSX/PDF export.
// Gated VIEW_INVOICE_FINANCIALS server-side; TPA-walled.
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiFetch, fmt } from "../api";
import { downloadReimbExport } from "./Reimbursements";

const DIMENSIONS = [
  { id: "insured", label: "Insured" },
  { id: "underwriter", label: "Underwriter" },
  { id: "broker", label: "Broker" },
];
const GROUP_OPTS = [
  { id: "party", label: "Party" },
  { id: "year", label: "Year" },
  { id: "month", label: "Month" },
];
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const METRIC_COLS = [
  { key: "claims", label: "Claims", align: "right", num: true },
  { key: "invoices", label: "Invoices", align: "right", num: true },
  { key: "hours", label: "Hours", align: "right", num: true, hours: true },
  { key: "service", label: "Service Fees", align: "right", money: true },
  { key: "expense", label: "Expenses", align: "right", money: true },
  { key: "admin", label: "Admin Fees", align: "right", money: true },
  { key: "total", label: "Total Billed", align: "right", money: true, strong: true },
];
// Group columns rendered (in fixed party→year→month order) when that dim is grouped.
const GROUP_COL = {
  party: { key: "party", label: "Party", align: "left", render: r => r.party || "—" },
  year: { key: "year", label: "Year", align: "right", num: true, render: r => (r.year != null ? r.year : "—") },
  month: { key: "month", label: "Month", align: "left", render: r => (r.month ? MONTHS[r.month] : "—") },
};

const fmtCell = (col, r) => {
  if (col.render) return col.render(r);
  const v = r[col.key];
  return col.money ? fmt.money(v || 0) : col.hours ? (Number(v) || 0).toFixed(2) : (v == null ? "—" : v);
};

export default function ProductionByParty({ initialViewState, onViewStateChange } = {}) {
  const iv = initialViewState || {};   // REPORTS-UX-1: restore a saved/pinned view
  const [dimension, setDimension] = useState(iv.dimension ?? "insured");
  const [groupBy, setGroupBy] = useState(iv.groupBy ?? ["party"]);
  const [dateFrom, setDateFrom] = useState(iv.dateFrom ?? "");
  const [dateTo, setDateTo] = useState(iv.dateTo ?? "");
  const [nameFilter, setNameFilter] = useState(iv.nameFilter ?? "");
  const [data, setData] = useState({ rows: [], grand: {} });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [sortKey, setSortKey] = useState(iv.sortKey ?? "total");
  const [sortDir, setSortDir] = useState(iv.sortDir ?? "desc");

  // REPORTS-UX-1: emit serialized view state up for pin/recent/saved-view capture.
  useEffect(() => {
    onViewStateChange && onViewStateChange({ dimension, groupBy, dateFrom, dateTo, nameFilter, sortKey, sortDir });
    /* eslint-disable-next-line */
  }, [dimension, groupBy, dateFrom, dateTo, nameFilter, sortKey, sortDir]);

  // Columns = the grouped dimensions (party→year→month order) + metrics.
  const cols = useMemo(() => [
    ...["party", "year", "month"].filter(g => groupBy.includes(g)).map(g => GROUP_COL[g]),
    ...METRIC_COLS,
  ], [groupBy]);

  const qs = useCallback(() => {
    const p = new URLSearchParams({ dimension, group_by: groupBy.join(",") });
    if (dateFrom) p.set("date_from", dateFrom);
    if (dateTo) p.set("date_to", dateTo);
    return p;
  }, [dimension, groupBy, dateFrom, dateTo]);

  const run = useCallback(() => {
    setLoading(true); setErr(null);
    apiFetch(`/reports/production-by-party?${qs().toString()}`)
      .then(d => setData(d)).catch(e => setErr(String(e.message || e))).finally(() => setLoading(false));
  }, [qs]);
  useEffect(() => { run(); }, [run]);

  // When the grouping changes, default the sort to the first group dim (so it
  // reads grouped) — except party-only, where biggest-business-first is handier.
  useEffect(() => {
    if (groupBy.length === 1 && groupBy[0] === "party") { setSortKey("total"); setSortDir("desc"); }
    else { setSortKey(groupBy[0]); setSortDir("asc"); }
  }, [groupBy]);

  const toggleGroup = (id) => setGroupBy(prev =>
    prev.includes(id) ? (prev.length > 1 ? prev.filter(x => x !== id) : prev) : [...prev, id]);

  const onSort = (key) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir((key === "party" || key === "month") ? "asc" : "desc"); }
  };

  const rows = useMemo(() => {
    let r = data.rows || [];
    if (nameFilter.trim() && groupBy.includes("party")) {
      const q = nameFilter.trim().toLowerCase();
      r = r.filter(x => (x.party || "").toLowerCase().includes(q));
    }
    // composite sort: clicked column primary, then the remaining group dims asc
    const groupKeys = ["party", "year", "month"].filter(g => groupBy.includes(g));
    const order = [sortKey, ...groupKeys.filter(k => k !== sortKey)];
    const cmp = (a, b, key, dir) => {
      const av = a[key], bv = b[key];
      if (typeof av === "string" || typeof bv === "string") return String(av || "").localeCompare(String(bv || "")) * dir;
      return ((av || 0) - (bv || 0)) * dir;
    };
    return [...r].sort((a, b) => {
      for (let i = 0; i < order.length; i++) {
        const dir = i === 0 ? (sortDir === "asc" ? 1 : -1) : 1;
        const c = cmp(a, b, order[i], dir);
        if (c) return c;
      }
      return 0;
    });
  }, [data.rows, nameFilter, sortKey, sortDir, groupBy]);

  const g = useMemo(() => {
    const t = { claims: 0, invoices: 0, hours: 0, service: 0, expense: 0, admin: 0, total: 0 };
    for (const r of rows) for (const k of Object.keys(t)) t[k] += (Number(r[k]) || 0);
    return t;
  }, [rows]);

  const th = (col) => ({
    position: "sticky", top: 0, zIndex: 2, background: "#e8ecf2", cursor: "pointer",
    padding: "9px 12px", fontSize: 12, fontWeight: 700, color: "var(--text1,#1a2438)",
    textAlign: col.align, borderBottom: "2px solid var(--border,#cbd2dd)", whiteSpace: "nowrap",
  });
  const td = (col) => ({
    padding: "7px 12px", fontSize: 13, textAlign: col.align,
    fontFamily: col.num || col.money ? "JetBrains Mono, monospace" : "inherit",
    fontWeight: col.strong ? 700 : 400, whiteSpace: "nowrap",
  });
  const arrow = (k) => k === sortKey ? (sortDir === "asc" ? " ▲" : " ▼") : "";
  const showParty = groupBy.includes("party");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Filter strip */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "10px 12px", background: "#f7f7f9", border: "1px solid var(--border)", borderRadius: 4 }}>
        <span style={{ display: "inline-flex", border: "1px solid var(--border)", borderRadius: 4, overflow: "hidden", background: "white" }}>
          {DIMENSIONS.map(d => (
            <button key={d.id} onClick={() => setDimension(d.id)}
              style={{ padding: "6px 14px", border: "none", fontSize: 12, fontWeight: 500, cursor: "pointer",
                background: dimension === d.id ? "var(--navy)" : "white", color: dimension === d.id ? "white" : "var(--text2)" }}>{d.label}</button>
          ))}
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="Invoice date from"
            style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 4 }} />
          <span style={{ fontSize: 12, color: "var(--text3)" }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} title="Invoice date to"
            style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 4 }} />
          {(dateFrom || dateTo) ? <button className="btn btn-outline btn-sm" onClick={() => { setDateFrom(""); setDateTo(""); }}>all-time</button> : <span style={{ fontSize: 11, color: "var(--text3)" }}>(all-time)</span>}
        </span>
        {showParty && <input value={nameFilter} onChange={e => setNameFilter(e.target.value)} placeholder={`Filter ${dimension}…`}
          style={{ padding: "6px 8px", border: "1px solid var(--border)", borderRadius: 4, minWidth: 170 }} />}
        <button className="btn btn-blue btn-sm" onClick={run} disabled={loading}>{loading ? "Running…" : "Run"}</button>
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
          <button className="btn btn-outline btn-sm" onClick={() => downloadReimbExport(`/reports/production-by-party/export?format=xlsx&${qs().toString()}`)} title="Export XLSX">XLSX</button>
          <button className="btn btn-outline btn-sm" onClick={() => downloadReimbExport(`/reports/production-by-party/export?format=pdf&${qs().toString()}`)} title="Export PDF">PDF</button>
        </span>
      </div>

      {/* Group-by toggles */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f7f7f9", border: "1px solid var(--border)", borderRadius: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: ".06em" }}>Break out by</span>
        {GROUP_OPTS.map(o => {
          const on = groupBy.includes(o.id);
          return (
            <button key={o.id} onClick={() => toggleGroup(o.id)}
              style={{ padding: "5px 13px", fontSize: 12, fontWeight: 600, borderRadius: 14, cursor: "pointer",
                border: "1px solid " + (on ? "var(--navy)" : "var(--border)"), background: on ? "var(--navy)" : "white", color: on ? "white" : "var(--text2)" }}>
              {on ? "✓ " : ""}{o.label}</button>
          );
        })}
        <span style={{ fontSize: 11, color: "var(--text3)" }}>· click a column header to sort (group dims tie-break automatically)</span>
      </div>

      {err && <div style={{ padding: "10px 12px", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 4, color: "#991b1b", fontSize: 13 }}>{err}</div>}

      {/* Summary band */}
      {!err && (
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", padding: "10px 14px", background: "var(--navy)", color: "#fff", borderRadius: 6, fontSize: 13 }}>
          <span><b style={{ fontSize: 18, fontFamily: "JetBrains Mono,monospace" }}>{fmt.money(g.total || 0)}</b> total billed</span>
          <span style={{ opacity: 0.85 }}>Service {fmt.money(g.service || 0)}</span>
          <span style={{ opacity: 0.85 }}>Expenses {fmt.money(g.expense || 0)}</span>
          <span style={{ opacity: 0.85 }}>Admin {fmt.money(g.admin || 0)}</span>
          <span style={{ opacity: 0.85 }}>{(g.hours || 0).toFixed(0)} hrs</span>
          <span style={{ marginLeft: "auto", opacity: 0.85 }}>{rows.length} row{rows.length === 1 ? "" : "s"}</span>
        </div>
      )}

      {/* Sticky sortable table */}
      {!err && (
        <div style={{ maxHeight: "60vh", overflow: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{cols.map(c => (
              <th key={c.key} style={th(c)} onClick={() => onSort(c.key)}>{c.label}{arrow(c.key)}</th>
            ))}</tr></thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ borderBottom: "1px solid #eef0f4" }}>
                  {cols.map(c => <td key={c.key} style={td(c)}>{fmtCell(c, r)}</td>)}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={cols.length} style={{ padding: "22px 12px", textAlign: "center", color: "var(--text3)", fontStyle: "italic", fontSize: 13 }}>
                  {dimension === "broker" ? "No broker parties are captured on claims yet — brokers aren't currently linked as a claim party." : (loading ? "Loading…" : "No business found for this filter.")}
                </td></tr>
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot><tr style={{ position: "sticky", bottom: 0, background: "#f1f5f9", fontWeight: 700 }}>
                {cols.map((c, idx) => (
                  <td key={c.key} style={{ ...td(c), borderTop: "2px solid var(--border)" }}>
                    {idx === 0 ? `Total (${rows.length})` : (METRIC_COLS.includes(c) ? fmtCell(c, g) : "")}
                  </td>
                ))}
              </tr></tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}
